import React from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatDateTimeFR } from '../../utils/dateUtils';

const SLOT_STYLES = {
  available: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  pending: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  confirmed: 'bg-green-50 border-green-200 text-green-800',
};

const SLOT_LABELS = {
  available: 'Disponible',
  pending: 'En attente',
  confirmed: 'Confirmé',
};

const AppointmentDayPanel = ({
  selectedDate,
  dayMeta,
  events = [],
  selectedAppointment,
  onSelectAppointment,
  onConfirm,
  onReschedule,
  onCancel,
}) => {
  if (!selectedDate) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
        Sélectionnez un jour dans le calendrier pour voir les créneaux et les rendez-vous.
      </div>
    );
  }

  const dateLabel = format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr });
  const capitalizedDate = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  if (!dayMeta?.is_working_day) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 capitalize">{capitalizedDate}</h3>
        <p className="mt-3 text-sm text-gray-500">Jour non ouvré — aucun créneau disponible.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 capitalize">{capitalizedDate}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {dayMeta.slots.filter((s) => s.status === 'available').length} créneaux libres sur {dayMeta.slots.length}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
        <div className="p-4 max-h-[520px] overflow-y-auto">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Créneaux</h4>
          <div className="space-y-2">
            {dayMeta.slots.map((slot) => {
              const linkedEvent = events.find(
                (e) => e.time === slot.time && e.date === dayMeta.date
              );
              const isSelected = selectedAppointment?.id === linkedEvent?.id;

              return (
                <button
                  key={slot.time}
                  type="button"
                  disabled={slot.status === 'available'}
                  onClick={() => linkedEvent && onSelectAppointment(linkedEvent)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition ${
                    SLOT_STYLES[slot.status]
                  } ${isSelected ? 'ring-2 ring-primary-500' : ''} ${
                    slot.status === 'available' ? 'cursor-default' : 'hover:opacity-90'
                  }`}
                >
                  <span className="font-medium">{slot.time}</span>
                  <span>{SLOT_LABELS[slot.status]}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Rendez-vous du jour</h4>
          {events.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun rendez-vous ce jour-là.</p>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => onSelectAppointment(event)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selectedAppointment?.id === event.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">{event.subject}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      event.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {event.status === 'confirmed' ? 'Confirmé' : 'En attente'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {event.parent_prenom} {event.parent_nom}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDateTimeFR(event.date, event.time)}
                  </p>
                </button>
              ))}
            </div>
          )}

          {selectedAppointment && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              <h5 className="font-semibold text-gray-900">{selectedAppointment.subject}</h5>
              <p className="text-sm text-gray-600 mt-1">
                {selectedAppointment.parent_prenom} {selectedAppointment.parent_nom}
                {selectedAppointment.parent_email ? ` — ${selectedAppointment.parent_email}` : ''}
              </p>
              {selectedAppointment.message && (
                <p className="text-sm text-gray-500 mt-2">{selectedAppointment.message}</p>
              )}
              <p className="text-sm mt-2">
                <strong>Proposé :</strong>{' '}
                {formatDateTimeFR(selectedAppointment.proposed_date, selectedAppointment.proposed_time)}
              </p>
              {selectedAppointment.confirmed_date && (
                <p className="text-sm text-green-700">
                  <strong>Confirmé :</strong>{' '}
                  {formatDateTimeFR(selectedAppointment.confirmed_date, selectedAppointment.confirmed_time)}
                </p>
              )}
              {selectedAppointment.staff_notes && (
                <p className="text-xs text-gray-500 mt-2 italic">Note : {selectedAppointment.staff_notes}</p>
              )}

              {selectedAppointment.status === 'pending' && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onConfirm(selectedAppointment.id)}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                  >
                    Confirmer
                  </button>
                  <button
                    type="button"
                    onClick={() => onReschedule(selectedAppointment.id)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                  >
                    Autre créneau
                  </button>
                  <button
                    type="button"
                    onClick={() => onCancel(selectedAppointment.id)}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                  >
                    Annuler
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentDayPanel;
